import { useMemo, useState } from "react";
import { Bookmark, TrendingUp, TrendingDown, Plus } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { demoWatchlists } from "@/lib/landing-demo-data";

const formatPrice = (price: number) => {
  if (price >= 1000) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

function resolveLogoUrl(url?: string): string | null {
  if (!url) return null;
  if (!url.includes("img.logo.dev")) return url;

  try {
    const parsed = new URL(url);
    const token = import.meta.env?.VITE_LOGO_DEV_TOKEN as string | undefined;
    if (token && !parsed.searchParams.has("token")) {
      parsed.searchParams.set("token", token);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function WatchlistAvatar({
  symbol,
  name,
  logo,
}: {
  symbol: string;
  name: string;
  logo?: string | null;
}) {
  const [hasError, setHasError] = useState(false);
  const logoUrl = useMemo(() => resolveLogoUrl(logo ?? undefined), [logo]);

  if (!logoUrl || hasError) {
    return (
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs border border-border/50 shrink-0">
        {symbol.slice(0, 2)}
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={`${name} logo`}
      className="h-9 w-9 rounded-full border border-border/50 bg-background object-contain p-1 shrink-0"
      loading="lazy"
      decoding="async"
      onError={() => setHasError(true)}
    />
  );
}

export default function WatchlistShowcase() {
  return (
    <section className="w-full py-20 bg-background">
      <div className="container px-4 md:px-6 mx-auto">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest">
            <Bookmark className="w-3 h-3" />
            Watchlists
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-serif">
            Keep Track of{" "}
            <span className="text-primary">What Matters</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Organize your favorite stocks, ETFs, and cryptocurrencies into custom 
            watchlists with real-time price updates.
          </p>
        </div>

        {/* Watchlist Cards */}
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {demoWatchlists.map((watchlist, watchlistIndex) => (
              <Card
                key={watchlist.name}
                className="flex flex-col h-[320px] transition-all hover:shadow-xl hover:-translate-y-1 border-border/50"
              >
                {/* Header */}
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between border-b shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Bookmark
                      className={cn(
                        "h-4 w-4 shrink-0 text-primary",
                        watchlistIndex === 0 && "fill-primary"
                      )}
                    />
                    <h3 className="font-semibold text-sm truncate">
                      {watchlist.name}
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      ({watchlist.items.length})
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 p-0 flex flex-col min-h-0">
                  <ScrollArea className="flex-1">
                    <div className="p-3 space-y-2">
                      {watchlist.items.map((item) => {
                        const isPositive = item.change >= 0;
                        return (
                          <div
                            key={item.symbol}
                            className="group flex items-center gap-3 rounded-lg border border-border/50 bg-card p-2.5 transition-all hover:shadow-sm hover:border-primary/30 cursor-pointer"
                          >
                            <WatchlistAvatar
                              symbol={item.symbol}
                              name={item.name}
                              logo={item.logo}
                            />

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs">
                                {item.symbol}
                              </span>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {item.name}
                              </p>
                            </div>

                            {/* Price & Change */}
                            <div className="flex flex-col items-end shrink-0">
                              <span className="text-xs font-mono font-medium">
                                {formatPrice(item.price)}
                              </span>
                              <span
                                className={cn(
                                  "text-[10px] font-semibold flex items-center gap-0.5",
                                  isPositive ? "text-emerald-500" : "text-red-500"
                                )}
                              >
                                {isPositive ? (
                                  <TrendingUp className="h-2.5 w-2.5" />
                                ) : (
                                  <TrendingDown className="h-2.5 w-2.5" />
                                )}
                                {isPositive ? "+" : ""}
                                {item.change.toFixed(2)}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Create Watchlist CTA */}
          <div className="flex justify-center mt-8">
            <Button variant="outline" size="lg" className="gap-2" asChild>
              <a href="/login">
                <Plus className="h-4 w-4" />
                Create Your Own Watchlists
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
